import React, { useEffect, useState } from 'react'
import {
  BaseFieldPanel,
  getPromptPlaceholder,
  isSmartFieldType,
} from './BaseFieldPanel'
import { getDisplayType, getInternalType } from './types'

export interface EditFieldPanelProps {
  onBack: () => void
  onClose: () => void
  onShowFieldType: () => void
  showFieldType: boolean
  onHideFieldType: () => void
  fieldTypePanelRef: React.RefObject<HTMLDivElement>
  editFieldPanelRef: React.RefObject<HTMLFormElement>
  onTypeChange: (_type: string) => void
  currentColumnType: string
  currentFieldName?: string
  currentPrompt?: string
  currentRunInBackground?: boolean
  onSave: (fieldData: {
    name: string
    type: string
    aiAutofill?: boolean
    prompt?: string
  }) => Promise<void>
  isSaving?: boolean
}

export function EditFieldPanel({
  onBack,
  onClose,
  onShowFieldType,
  showFieldType,
  onHideFieldType,
  fieldTypePanelRef,
  editFieldPanelRef,
  onTypeChange,
  currentColumnType,
  currentFieldName = '',
  currentPrompt,
  currentRunInBackground,
  onSave,
  isSaving = false,
}: EditFieldPanelProps) {
  const [aiAutofill, setAiAutofill] = useState(currentRunInBackground || false)
  const [fieldName, setFieldName] = useState(currentFieldName)
  const [selectedFieldType, setSelectedFieldType] = useState(currentColumnType)
  const [hasChanges, setHasChanges] = useState(false)
  const [promptValue, setPromptValue] = useState(currentPrompt || '')

  const [error, setError] = useState<string | null>(null)

  const displayType = getDisplayType(selectedFieldType)

  // Update local state when props change
  useEffect(() => {
    setFieldName(currentFieldName)
    setSelectedFieldType(currentColumnType)
    setPromptValue(currentPrompt || '')
    setAiAutofill(currentRunInBackground || false)
  }, [
    currentFieldName,
    currentColumnType,
    currentPrompt,
    currentRunInBackground,
  ])

  const handleTypeSelect = (type: string) => {
    const internalType = getInternalType(type)
    setSelectedFieldType(internalType)
    setHasChanges(true)
    onHideFieldType()
  }

  const handleSave = async () => {
    if (!fieldName.trim()) {
      setError('Field name is required')
      return
    }

    setError(null)

    try {
      await onSave({
        name: fieldName.trim(),
        type: selectedFieldType,
        aiAutofill,
        prompt: promptValue.trim() || undefined,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field')
    }
  }

  const handleCancel = () => {
    // Reset to original values
    setSelectedFieldType(currentColumnType)
    setFieldName(currentFieldName)
    setAiAutofill(currentRunInBackground || false)
    setHasChanges(false)
    setPromptValue(currentPrompt || '')
    setError(null)
    onBack()
  }

  const isSmartType = isSmartFieldType(displayType)

  // Check for changes
  useEffect(() => {
    const hasFieldChanges =
      fieldName !== currentFieldName ||
      selectedFieldType !== currentColumnType ||
      aiAutofill !== (currentRunInBackground || false) ||
      (isSmartType && promptValue.trim() !== (currentPrompt || '').trim())

    setHasChanges(hasFieldChanges)
  }, [
    fieldName,
    selectedFieldType,
    aiAutofill,
    promptValue,
    currentFieldName,
    currentColumnType,
    currentPrompt,
    isSmartType,
    currentRunInBackground,
  ])

  return (
    <BaseFieldPanel
      onBack={handleCancel}
      onClose={onClose}
      onShowFieldType={onShowFieldType}
      showFieldType={showFieldType}
      onHideFieldType={onHideFieldType}
      fieldTypePanelRef={fieldTypePanelRef}
      panelRef={editFieldPanelRef}
      title='Edit field'
      selectedFieldType={displayType}
      fieldName={fieldName}
      onFieldNameChange={setFieldName}
      onTypeSelect={handleTypeSelect}
      onSave={handleSave}
      isSaving={isSaving}
      saveButtonText='Save'
      showCancelButton={hasChanges}
      onCancel={handleCancel}
      hasChanges={hasChanges}
      error={error}
      onErrorClear={() => setError(null)}
      aiAutofill={aiAutofill}
      onAiAutofillChange={setAiAutofill}
      promptValue={promptValue}
      onPromptChange={setPromptValue}
      showPromptInput={isSmartType}
      getPromptPlaceholder={() => getPromptPlaceholder(displayType)}
    />
  )
}
